/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { useCloudflareAuth } from '../../../contexts/CloudflareAuthContext';
import api from '../../../services/api';
import { toast } from 'react-hot-toast';
import { FaDownload, FaSync } from 'react-icons/fa';
import { logger } from '../../../utils/logger';
import { buildMemberQrValue } from '../../../utils/memberQr';

interface MemberDetails {
  fullName: string;
  email: string;
  permanentEnrollmentNumber: string;
  memberType: string;
  designation: string;
  phone: string;
  languagesKnown: string;
  memberStatus: string;
  currentState: string;
  currentDistrict: string;
  currentCity: string;
  currentPinCode: string;
  dateOfBirth: string;
  bloodGroup: string;
  fatherOrHusbandName: string;
  branch: string;
  validUntil: string;
}

const QRCodePage = () => {
  const { currentUser } = useCloudflareAuth();
  const [memberDetails, setMemberDetails] = useState<MemberDetails | null>(null);
  const qrRef = useRef<SVGSVGElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMemberDetails = useCallback(async (userId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.getUser(userId);
      const data = response.details || response.user;

      if (!data) {
        setError('No user document found');
        return;
      }

      setMemberDetails({
        fullName: data.full_name || data.fullName || '',
        email: data.auth_email || data.authEmail || data.email || '',
        permanentEnrollmentNumber: data.enrollment_number || data.enrollmentNumber || '',
        memberType: data.member_type || data.memberType || '',
        designation: data.designation || '',
        phone: data.phone || '',
        languagesKnown: data.languages_known || data.languagesKnown || '',
        memberStatus: data.member_status || data.memberStatus || '',
        currentState: data.current_state || data.currentState || '',
        currentDistrict: data.current_district || data.currentDistrict || '',
        currentCity: data.current_city_village || data.currentCityVillage || '',
        currentPinCode: data.current_pin_code || data.currentPinCode || '',
        dateOfBirth: data.date_of_birth || data.dateOfBirth || '',
        bloodGroup: data.blood_group || data.bloodGroup || '',
        fatherOrHusbandName: data.father_or_husband_name || data.fatherOrHusbandName || '',
        branch: data.branch || '',
        validUntil: data.valid_until || data.validUntil || ''
      });
    } catch (error) {
      setError('Failed to fetch member details');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchMemberDetails(currentUser.uid);
    } else {
      setError('Please log in to view QR code');
      setLoading(false);
    }
  }, [currentUser, fetchMemberDetails]);

  const getQRValue = () => {
    if (!memberDetails) return '';
    return buildMemberQrValue({ ...memberDetails, email: memberDetails.email || currentUser?.email || '' });
  };

  const downloadQRCode = () => {
    const svg = qrRef.current;
    if (svg) {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const svgData = new XMLSerializer().serializeToString(svg);
      const img = new Image();
      const svgUrl = URL.createObjectURL(new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' }));
      
      img.onload = () => {
        canvas.width = 900;
        canvas.height = 900;
        if (ctx) {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        }
        
        const pngUrl = canvas.toDataURL('image/png');
        const downloadLink = document.createElement('a');
        downloadLink.href = pngUrl;
        downloadLink.download = `qrcode_${memberDetails?.permanentEnrollmentNumber || 'member'}.png`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(svgUrl);
        toast.success('QR Code downloaded successfully!');
      };
      img.onerror = () => {
        URL.revokeObjectURL(svgUrl);
        toast.error('Failed to generate QR code image');
      };
      img.src = svgUrl;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            <FaSync className="mr-2" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!memberDetails) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">No member details found</p>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            <FaSync className="mr-2" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 py-8">
      <div className="mx-auto max-w-4xl px-3 sm:px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl bg-gray-800 p-4 sm:p-8"
        >
          <h1 className="text-2xl font-bold text-white mb-6">Member QR Code</h1>
          
          <div className="flex justify-center">
            {/* QR Code Section */}
            <div className="flex w-full flex-col items-center space-y-6">
              <div className="w-full max-w-[384px] rounded-xl bg-white p-4 shadow-lg sm:p-8">
                <QRCodeSVG
                  ref={qrRef}
                  value={getQRValue()}
                  size={320}
                  bgColor="#FFFFFF"
                  fgColor="#000000"
                  level="M"
                  includeMargin={true}
                  marginSize={4}
                  className="h-auto w-full"
                />
              </div>

              <button
                onClick={downloadQRCode}
                className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
              >
                <FaDownload className="mr-2" />
                Download QR Code
              </button>
            </div>

          </div>

          {/* Instructions */}
          <div className="mt-8 bg-blue-500/10 text-blue-300 rounded-lg p-6">
            <h3 className="font-semibold mb-2">How to use:</h3>
            <ol className="list-decimal list-inside space-y-1">
              <li>Open any QR code scanner app</li>
              <li>Point camera at the QR code</li>
              <li>Hold steady until it scans</li>
              <li>View your member details</li>
            </ol>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default QRCodePage;
